import tutor from 'assets/images/characters/CarolinaMariadeJesus/waving-happily.webp';
import {Message} from 'APIs/aiTutorAPI/messages/types';
import {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {createAiTutorSessionRequest} from 'APIs/aiTutorAPI/session';
import {sendAiTutorMessageRequest} from 'APIs/aiTutorAPI/chat';
import {fetchAiTutorMessagesRequest} from 'APIs/aiTutorAPI/messages';
import {getUserData} from 'utils/functions/getUserData/getUserData';
import {SessionPayload} from 'APIs/aiTutorAPI/session/types';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button} from 'letrus-ui';
import {QuestionnaireStep} from 'APIs/letrusAPI/questionnaire/types';
import TypeWritter from 'components/ui/TypeWritter';
import {ChatResponse} from 'APIs/aiTutorAPI/chat/types';
import styles from './AiTutorChat.module.scss';

export interface AiTutorChatProps {
  questionId: number;
  finishedSessionId?: number;
  prevQuestionAnswer?: string;
  onError(error: string): void;
  settings: QuestionnaireStep['question']['settings'];
  onComplete(sessionId?: number, report?: ChatResponse['report']): void;
}

interface ChatMessage extends Message {
  id: number;
}

function AiTutorChat({
  onError,
  settings,
  questionId,
  onComplete,
  finishedSessionId,
  prevQuestionAnswer,
}: AiTutorChatProps): JSX.Element {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [activeSession, setActiveSession] = useState(finishedSessionId);

  const {user} = getUserData();
  const form = useForm({defaultValues: {userInput: ''}});

  const session = createAiTutorSessionRequest();
  const aiTutorChat = sendAiTutorMessageRequest();
  const messageHistory = fetchAiTutorMessagesRequest(activeSession, {
    onSuccess: setupInitialMessages,
    isPaused: () => !!messages.length,
  });

  const isSessionFinished =
    !!finishedSessionId ||
    session.data?.status === 'completed' ||
    aiTutorChat.data?.status === 'completed';

  useEffect(() => {
    createSession();
  }, []);

  useEffect(() => {
    if (activeSession && !isSessionFinished && prevQuestionAnswer) {
      aiTutorChat.trigger(
        {
          user_message: `Resposta escolhida: ${prevQuestionAnswer}`,
          session_id: activeSession,
        },
        {
          onError: () => onError('não foi possível enviar a mensagem'),
          onSuccess: ({response}) =>
            updateMessages({role: 'ai', content: response}),
        },
      );
    }
  }, [prevQuestionAnswer, activeSession]);

  useEffect(() => {
    if (isSessionFinished && activeSession) {
      onComplete(activeSession, aiTutorChat.data?.report);
    }
  }, [isSessionFinished, activeSession]);

  function createSession() {
    const hasPreviousSession = !!activeSession;

    const isAbleToCreate =
      settings?.agent_id && user?.id && questionId && !session.isMutating;

    if (isAbleToCreate && !hasPreviousSession) {
      const payload: SessionPayload = {
        user_id: user.id,
        question_id: questionId,
        agent_id: settings?.agent_id,
        settings: {nome_estudante: user.first_name, ...settings},
      };

      session.trigger(payload, {
        onError: () => onError('atividade indisponível'),
        onSuccess: ({session_id}) => setActiveSession(session_id),
      });
    }
  }

  function setupInitialMessages(messages: Message[]) {
    setMessages(
      messages.map((msg, index) => ({id: index + 1, ...msg})).reverse(),
    );
  }

  function updateMessages(message: Message) {
    setMessages((prev) => [
      {
        ...message,
        id: prev.length + 1,
        content: message.content,
      },
      ...prev,
    ]);
  }

  function sendToAiTutor({userInput}: {userInput: string}) {
    if (!session.data || isSessionFinished) return;
    const {session_id} = session.data;

    updateMessages({role: 'user', content: userInput});

    form.reset();

    aiTutorChat.trigger(
      {user_message: userInput, session_id},
      {
        onError: () => onError('não foi possível enviar a mensagem'),
        onSuccess: ({response}) =>
          updateMessages({role: 'ai', content: response}),
      },
    );
  }

  function handleKeyPress(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    const userInput = form.getValues('userInput');

    if (event.key === 'Enter' && userInput.trim() !== '') {
      event.preventDefault();
      sendToAiTutor({userInput});
    }
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.messageHistory}>
        {messages.map((msg, index) => (
          <div key={msg.id} className={`${styles.message} ${styles[msg.role]}`}>
            {msg.role === 'ai' && (
              <img src={tutor} alt="Tutor acenando" className={styles.avatar} />
            )}
            <div className={styles.chatBubble}>
              <TypeWritter
                text={msg.content}
                delay={25}
                disabled={
                  msg.role === 'user' || index !== 0 || isSessionFinished
                }
              />
            </div>
          </div>
        ))}
      </div>

      <span className={styles.loading}>
        {aiTutorChat.isMutating ? 'Pensando na resposta...' : ''}
      </span>

      <form className={styles.form} onSubmit={form.handleSubmit(sendToAiTutor)}>
        <textarea
          onKeyPress={handleKeyPress}
          className={styles.textarea}
          onPaste={(event) => event.preventDefault()}
          disabled={
            !activeSession || isSessionFinished || aiTutorChat.isMutating
          }
          placeholder={
            !messageHistory.data?.length
              ? 'Escreva sua mensagem para Carolina'
              : 'Digite uma nova mensagem'
          }
          {...form.register('userInput')}
        />

        <Button
          rounded
          type="submit"
          aria-label="Enviar"
          disabled={
            !activeSession || isSessionFinished || aiTutorChat.isMutating
          }
        >
          <FontAwesomeIcon icon="arrow-up" className={styles.icon} />
        </Button>
      </form>
    </div>
  );
}

export default AiTutorChat;
